type PersistentDataValue = string | number | object | boolean;

export class PersistantData {
  storage: Storage;
  keyPrefix: string;

  constructor(targetStorage: Storage, keyPrefix?: string | null) {
    this.storage = targetStorage;
    this.keyPrefix = keyPrefix || "";
  }

  public clear() {
    this.storage.clear();
  }

  /**
   * Saves a value to the storage. Note that if the value is not a simple type it'll be serialized to json.
   *
   * Returns true if the value was saved successfully.
   * @param key
   * @param value
   */
  public set(
    key: string,
    value: PersistentDataValue | PersistentDataValue[]
  ): boolean {
    let serializedVal =
      typeof value === "object" ? JSON.stringify(value) : value.toString();

    try {
      this.storage.setItem(this.keyPrefix + key, serializedVal);
      return true;
    } catch (err) {
      // quota exceeded, either the maximum storage size was reached or the user disabled storage for this site.
      return false;
    }
  }

  public get(key: string): string | null {
    return this.storage.getItem(this.keyPrefix + key);
  }

  public remove(key: string) {
    this.storage.removeItem(this.keyPrefix + key);
  }

  /**
   * Gets the string value for the key parameter and parses it as json if it exists.
   * Note that there's no guarantee that the object matches the layout of type T.
   *
   * The object will also be limited to what json can deserialize to,
   * so if you try to deserialize to a class it won't become an instance of that class.
   */
  public deserialize<T extends { [key: string]: unknown } | unknown[]>(
    key: string
  ): T | null {
    const serializedValue = this.get(key);

    if (serializedValue == null) {
      return null;
    }

    return JSON.parse(serializedValue) as T;
  }
}

export const SessionStorage = new PersistantData(
  window.sessionStorage,
  "MedPlannerReact"
);
export const LocalStorage = new PersistantData(
  window.localStorage,
  "MedPlannerReact"
);
